/**
 * Google Analytics 4 with Consent Mode v2. gtag always loads; what it may store is declared first:
 * granted by default (Argentina and the rest of the world), denied in the EU, UK and Switzerland, and
 * the visitor's own choice (CookieBanner) wins everywhere. While denied, gtag sets no cookies and only
 * sends anonymous pings. The measurement id comes from the server (<meta name="pokefans:ga">); without
 * it, none of this runs.
 * E-commerce events are in pesos at the bank-transfer price (what the shop shows and Google Ads
 * optimizes for); the purchase uses what was actually charged. Dollars only while there is no rate.
 */

import { transferUnitArs } from "../../shared/money";

export type Consent = "granted" | "denied";

const CONSENT_KEY = "pf-cookie-consent";
export const CONSENT_EVENT = "pf-consent-change";

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

export function gaId(): string | null {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLMetaElement>('meta[name="pokefans:ga"]')?.content || null;
}

export function getConsent(): Consent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/** Where analytics needs an opt-in (EU and EEA, UK, Switzerland): denied until the visitor accepts. */
export const OPT_IN_REGIONS = ["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO", "GB", "CH"];

/** Everywhere else the default is granted (opt-out): the visitor can reject from the banner or the footer. */
const DEFAULT_OUTSIDE_OPT_IN: Consent = "granted";

/** The four Consent Mode v2 signals, all with the same value: one choice for everything. */
const signals = (v: Consent) => ({ ad_storage: v, analytics_storage: v, ad_user_data: v, ad_personalization: v });

let loaded = false;

function load(id: string) {
  if (loaded) return;
  loaded = true;
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js reads the `arguments` object itself, not an array.
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments);
  };
  // The consent state has to be in the dataLayer before `config`.
  window.gtag("consent", "default", { ...signals("denied"), region: OPT_IN_REGIONS });
  window.gtag("consent", "default", signals(DEFAULT_OUTSIDE_OPT_IN));
  // A stored choice wins over both defaults (a region default would win over a second global one).
  const stored = getConsent();
  if (stored) window.gtag("consent", "update", signals(stored));
  // Without ad cookies, the click id travels in internal links so a later conversion keeps it.
  window.gtag("set", "url_passthrough", true);
  window.gtag("js", new Date());
  // Page views are sent by hand on every route change (single-page app).
  window.gtag("config", id, { send_page_view: false });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
}

/**
 * Removes Google Analytics and Ads cookies (_ga, _ga_*, _gcl_*) when consent is withdrawn, and the
 * click data the Google tag keeps in local storage (_gcl_ls).
 */
function clearGaCookies() {
  try {
    for (const key of Object.keys(localStorage)) if (key.startsWith("_gcl_")) localStorage.removeItem(key);
  } catch {
    // private mode: nothing stored
  }
  const host = location.hostname;
  for (const part of document.cookie.split(";")) {
    const name = part.split("=")[0].trim();
    if (!/^(_ga(_|$)|_gcl_)/.test(name)) continue;
    for (const domain of ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`]) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
}

export function setConsent(value: Consent | null): void {
  const id = gaId();
  try {
    if (value) localStorage.setItem(CONSENT_KEY, value);
    else localStorage.removeItem(CONSENT_KEY);
  } catch {
    // private mode: the choice lasts this page only
  }
  // Reopening the notice (null) leaves gtag as it is. Loading first means the stored choice is
  // already in the defaults; the update below repeats it, which is harmless.
  if (id && value) {
    load(id);
    window.gtag?.("consent", "update", signals(value));
    if (value === "denied") clearGaCookies();
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

/** Call once at startup: loads gtag with the consent defaults (and the stored choice, if any). */
export function initAnalytics(): void {
  const id = gaId();
  if (id) load(id);
}

/** gtag decides what to send from the consent state; the site only needs an id. */
export function track(event: string, params: Record<string, unknown> = {}): void {
  const id = gaId();
  if (!id) return;
  load(id);
  window.gtag?.("event", event, params);
}

/**
 * An event right before leaving the site (the redirect to Mercado Pago): `then` runs once Analytics
 * has it, or after a short wait, or right away when nothing is measured.
 */
export function trackThen(event: string, params: Record<string, unknown>, then: () => void): void {
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    then();
  };
  const id = gaId();
  if (!id) return go();
  load(id);
  window.gtag?.("event", event, { ...params, event_callback: go });
  window.setTimeout(go, 600);
}

/** Query parameters worth measuring (search, shop filters, campaigns); everything else stays out. */
const KEEP_PARAMS = /^(q|game|category|set|rarity|sort|page|stock|min|max|desde|gclid|gbraid|wbraid|utm_[a-z]+)$/;

/**
 * The path as Analytics may see it: order access keys (?k=), emails, tokens and the like never go
 * to Google (personal data in page URLs is against its terms, and a key opens an order).
 */
export function analyticsPath(path: string): string {
  const [pathname, query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  const kept = new URLSearchParams([...params].filter(([k]) => KEEP_PARAMS.test(k)));
  return kept.size ? `${pathname}?${kept}` : pathname;
}

export function trackPageView(path: string): void {
  const clean = analyticsPath(path);
  track("page_view", { page_location: location.origin + clean, page_path: clean, page_title: document.title });
}

export interface GaItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
}

/** USD cents → dollars, as GA expects. */
export const usd = (cents: number) => Math.round(cents) / 100;

/** Prices for GA events: one currency per event, each unit at the transfer price in pesos. */
export interface GaPricer {
  currency: "ARS" | "USD";
  price: (usdCents: number, fixedArs?: number | null) => number;
}

export function gaPricer(money: { rate: number | null; mpFeePct: number } | null): GaPricer {
  if (!money?.rate) return { currency: "USD", price: (cents) => usd(cents) };
  const { rate, mpFeePct } = money;
  return { currency: "ARS", price: (cents, fixed) => transferUnitArs(cents, rate, fixed, mpFeePct) };
}

/** Event name of the Google Ads "Purchase" conversion (set up as a manual "page load" event in Ads). */
export const ADS_PURCHASE_EVENT = "manual_event_PURCHASE";

/** The purchase as charged: pesos (or dollars) including shipping and after discounts. */
export function purchaseValue(o: { chargeCurrency: "ARS" | "USD"; chargeAmountMinor: number | null; total: number }): { currency: "ARS" | "USD"; value: number } {
  return o.chargeAmountMinor !== null ? { currency: o.chargeCurrency, value: o.chargeAmountMinor / 100 } : { currency: "USD", value: usd(o.total) };
}
